"use client";

// FILE: src/features/nmck-calculator/components/calculator-form.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: Форма расчёта Начальной (Максимальной) Цены Контракта (НМЦК) для охранных услуг по Приказу Росгвардии №45.
// SCOPE: Многошаговая форма (Контракт → Посты и Опции → Результат → Запрос КП у ЧОП) с подстановкой МРОТ по регионам, бейджем типовой НМЦК региона, расчётом ночных/праздничных часов, коэффициентов усложнения, СТСО (только для объектов с обязательными требованиями к антитеррористической защищённости, 427-ФЗ), страхования, инфляции и НДС. Экспорт в CSV/HTML/PDF, шаринг по ссылке, анализ коэффициента вариации цен поставщиков.
// INPUT: Параметры контракта (даты, регион, МРОТ, СНР, коэфф.), параметры постов (кол-во, часы, коэфф. усложнения), фин. параметры (ставки, СТСО, страхование), цены поставщиков.
// OUTPUT: CalculateNmtckResponse — полный расчёт НМЦК; CSV/HTML/PDF-отчёты; ссылка для шаринга; запрос КП в ЧОП.
// KEYWORDS: DOMAIN(NMCC): ContractCalculation; DOMAIN(Regulation): RosgvardiaOrder45; TECH(React): ClientComponent; MATH(CoefficientOfVariation); MATH(MROT)
// STATES: [loading: спиннер загрузки формы/регионов; empty: нет постов — предложить добавить; error: ошибка расчёта с сообщением; data: форма расчёта с результатами]
// END_MODULE_CONTRACT
//
// START_RATIONALE:
// Q: Почему расчёт НМЦК происходит на клиенте (React), а не на сервере?
// A: Чтобы дать пользователю мгновенную обратную связь при изменении параметров (MROT, U-коэффициенты, количество постов). Серверный RPC calculate-nmtck используется только для финального "живого" расчёта при отправке формы.
// Q: Почему используется динамический импорт (next/dynamic) для формы?
// A: Библиотеки расчёта (date-fns, zod) тяжелые — динамический импорт сокращает размер初始 бандла на ~40KB. Форма рендерится только при скролле пользователя к калькулятору.
// END_RATIONALE
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.6.0 — 427-ФЗ: флаг «объект с обязательными требованиями к антитеррористической
//   защищённости» (antiterror_object) управляет слагаемым СТСО (выключен → поле заблокировано и
//   обнулено); бейдж «типовая НМЦК региона» под селектом региона на шаге 1 (lib/typical-nmck.ts).
// PREV_CHANGE_SUMMARY: 1.5.3 — Фикс регресса UI-верификации: поле «Срок (мес)» получило step="any"
//   (дробный billableMonths иначе блокировал submit HTML5-валидацией); подсказка «По вики: от N ₽/мес»
//   перенесена под поле абонплаты; под сроком — точный календарный label «= 13 мес 15 дн. по календарю».
// PREV_CHANGE_SUMMARY: 1.5.2 — Делитель дней 30.5 (решение владельца 2026-08-22); в params сохраняются
//   start_date/end_date (точный label ГБР на share/PDF); при клампе срока до 1 мес — пометка
//   «(минимум — 1 мес)» в блоке шага 3.
// PREV_CHANGE_SUMMARY: 1.5.1 — Срок ГБР: contractDuration (billableMonths) вместо округления вверх; label
//   «N мес M дн.» (formatGbrDuration) в блоке шага 3 и экспортах CSV/HTML/PDF; подсказка нижней границы
//   тарифа под полем абонплаты (обновляется при смене типа объекта).
// PREV_CHANGE_SUMMARY: 1.5.0 — Опция «Пультовая охрана с ГБР» (рыночная, вне Приказа №45): поля формы (gbr_*),
//   карточка в шаге 2, блок результата в шаге 3, персист/шаринг (details.params), PDF/HTML/CSV.
//   Превью uBase синхронизирован с RPC v4 (calculateUBase).
// PREV_CHANGE_SUMMARY: 1.4.0 — Настоящий calculation_id: ensureCalculationSaved (единая точка persist расчёта), кнопка «Сохранить» в шаге 3, запрос КП сохраняет расчёт перед переходом на шаг 4.
// PREV_CHANGE_SUMMARY: 1.3.0 — Добавлен Step 4 «Запрос КП у ЧОП»: интеграция с рейтингом ЧОП, поиск/замена, отправка запросов.
// PREV_CHANGE_SUMMARY: 1.2.0 — Добавлены быстрые пресеты (ТЦ/Завод/Школа/Склад/Офис) + интеграция CostBreakdownBar (структура цены: stacked bar, легенда, мета-карточки, детальная разбивка).
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// FUNC [10] Главная форма-компонент с шагами => CalculatorForm
// FUNC [7] Сабмит и агрегация расчёта по postConfigs => onSubmit
// FUNC [4] Экспорт в CSV => handleExportCSV
// FUNC [5] Экспорт в HTML-отчёт => handleExportHTML
// FUNC [4] Экспорт в PDF через API => handleExportPDF
// FUNC [4] Шаринг расчёта по ссылке => handleShare
// FUNC [5] Единая точка сохранения расчёта (persist в nmtck_calculations) => ensureCalculationSaved
// FUNC [3] Сохранение расчёта кнопкой шага 3 => handleSaveCalculation
// FUNC [3] Переход к запросу КП с сохранением расчёта => handleChopRequest
// FUNC [3] Расчёт коэффициента вариации цен => calculateVariation
// FUNC [2] Инлайн-тултип => InfoTooltip
// STATE [7] Массив конфигураций постов с индивидуальными U => postConfigs
// STATE [5] Массив результатов расчёта по каждому посту => perPostResults
// HOOK [6] Клиентский предварительный расчёт НМЦК => useNmtckPreview
// FUNC [5] Чистая функция предварительного расчёта => calculateNmtckPreview
// END_MODULE_MAP

import { useState, useEffect, useRef, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Loader2, Calculator, Info, Settings2, ShieldCheck, ShieldAlert, Sparkles, Download, Share2, Save, ArrowRight, ArrowLeft, BarChart3 } from "lucide-react";

import { Button } from "@oxpa/shared/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@oxpa/shared/ui/form";
import { Input } from "@oxpa/shared/ui/input";
import { Checkbox } from "@oxpa/shared/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@oxpa/shared/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@oxpa/shared/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@oxpa/shared/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@oxpa/shared/ui/tooltip";

import { calculateNmtckAction, CalculateNmtckResponse } from "../actions/calculate";
import { saveAndGetShareLink } from "../actions/share";
import { getNmckRegions, getNmckCalendars, NmckRegion, NmckCalendar } from "../actions/regions";
import { calculateContractHours } from "../lib/time-calculator";
import { GBR_OBJECT_TYPES, calcGbrTotal, contractDuration, formatGbrDuration, gbrDurationFromBillable } from "../lib/gbr";
import { calculateNmtckPreview, calculateUBase } from "../hooks/use-nmtck-preview";
import { TypicalNmckChip } from "./typical-nmck-chip";
import { CostBreakdownBar } from "./cost-breakdown-bar";
import { ChopRequestStep } from "./chop-request-step";

const formSchema = z.object({
  title: z.string().optional(),
  start_date: z.string().min(10),
  end_date: z.string().min(10),
  region_id: z.string().min(1),
  
  // Custom overrides (visible in advanced)
  mrot_override: z.coerce.number().min(0).optional(),
  snr_override: z.coerce.number().min(0).optional(),
  regional_coef: z.coerce.number().min(1).max(3),
  north_coef: z.coerce.number().min(0).max(100),
  
  insurance_rate: z.coerce.number().min(0).max(1),
  indirect_rate: z.coerce.number().min(0).max(1),
  profit_rate: z.coerce.number().min(0).max(1),

  // 427-ФЗ (с 01.09.2026): ТСО входят в НМЦК на охрану ТОЛЬКО на объектах с обязательными
  // требованиями к антитеррористической защищённости (п.4 ч.2 ст.5). Флаг управляет слагаемым СТСО.
  antiterror_object: z.boolean().default(false),
  stso_amount: z.coerce.number().min(0),
  insurance_amount: z.coerce.number().min(0),

  inflation_index: z.coerce.number().min(1).max(2),
  vat_percent: z.coerce.number().min(0).max(22),

  // Пультовая охрана с ГБР — рыночная опция, НЕ входит в методику Приказа №45
  gbr_enabled: z.boolean().default(false),
  gbr_object_type: z.string().default("office"),
  gbr_fee_monthly: z.coerce.number().min(0).default(7000),
  gbr_months: z.coerce.number().min(1).default(1),
  gbr_armed: z.boolean().default(false),
});

// START_PRESETS: Быстрые сценарии расчёта НМЦК
// Маппинг дизайн-прототипа (ТЦ/Завод/Школа/Склад/Офис) на реальную модель формы:
// posts_count + schedule_hours → postConfigs[0]; regionMatch → regions[].name;
// u-коэффициенты → подобраны по типу объекта (Приказ №45, Таблица №2).
interface NmckPreset {
  key: string;
  label: string;
  desc: string;
  posts_count: number;
  schedule_hours: number;
  regionMatch: string;
  u_special_eq: boolean;
  u_armed: boolean;
  u_mass_events: boolean;
  u_antiterror: boolean;
  u_state_secret: boolean;
}

const PRESETS: NmckPreset[] = [
  {
    key: "tc",
    label: "ТЦ",
    desc: "2 поста · 24ч · Москва",
    posts_count: 2,
    schedule_hours: 24,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: false,
    u_mass_events: true,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "zavod",
    label: "Завод",
    desc: "5 постов · 24ч · оружие",
    posts_count: 5,
    schedule_hours: 24,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: true,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "school",
    label: "Школа",
    desc: "1 пост · 12ч · антитеррор",
    posts_count: 1,
    schedule_hours: 12,
    regionMatch: "Москва",
    u_special_eq: false,
    u_armed: false,
    u_mass_events: true,
    u_antiterror: true,
    u_state_secret: false,
  },
  {
    key: "sklad",
    label: "Склад",
    desc: "8 постов · 24ч · Казань",
    posts_count: 8,
    schedule_hours: 24,
    regionMatch: "Казань",
    u_special_eq: true,
    u_armed: true,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
  {
    key: "office",
    label: "Офис",
    desc: "3 поста · 24ч · СПб",
    posts_count: 3,
    schedule_hours: 24,
    regionMatch: "Санкт-Петербург",
    u_special_eq: true,
    u_armed: false,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  },
];
// END_PRESETS

export const CalculatorForm = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("step1");
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<CalculateNmtckResponse | null>(null);
  const [supplierPrices, setSupplierPrices] = useState<number[]>([]);
  const [newSupplierPrice, setNewSupplierPrice] = useState<string>('');
  const [shareUrl, setShareUrl] = useState<string>('');
  const [shareLoading, setShareLoading] = useState(false);
  // <MEMENTO>2026-08-21: savedCalculationId — UUID сохранённого расчёта (INSERT nmtck_calculations).
  // Псевдо-id calc_<timestamp> ронял FK nmck_chop_requests.calculation_id. saveCalculationPromiseRef
  // кэширует выполняющийся INSERT: параллельные вызовы (Поделиться + Сохранить + Запрос КП) не
  // дублируют запись. Сброс при пересчёте — useEffect на result ниже.</MEMENTO>
  const [savedCalculationId, setSavedCalculationId] = useState<string | null>(null);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const saveCalculationPromiseRef = useRef<Promise<string> | null>(null);
  interface PostConfig {
    id: string;
    posts_count: number;
    schedule_hours: number;
    u_special_eq: boolean;
    u_armed: boolean;
    u_mass_events: boolean;
    u_antiterror: boolean;
    u_state_secret: boolean;
  }
  
  const [postConfigs, setPostConfigs] = useState<PostConfig[]>([{
    id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '1',
    posts_count: 1,
    schedule_hours: 24,
    u_special_eq: false,
    u_armed: false,
    u_mass_events: false,
    u_antiterror: false,
    u_state_secret: false,
  }]);
  
  const [perPostResults, setPerPostResults] = useState<CalculateNmtckResponse[]>([]);
  
  const [regions, setRegions] = useState<NmckRegion[]>([]);
  const [calendars, setCalendars] = useState<NmckCalendar[]>([]);
  
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split("T")[0]; // Next month 1st
  const defaultEndDate = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0).toISOString().split("T")[0]; // Next month last day

  type FormValues = z.infer<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      title: "Охрана объекта (по Приказу №45)",
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      region_id: "",
      regional_coef: 1.0,
      north_coef: 0,
      
      insurance_rate: 0.302, // 30.2%
      indirect_rate: 0.2, // 20%
      profit_rate: 0.05, // 5%

      antiterror_object: false,
      stso_amount: 0,
      insurance_amount: 0,

      inflation_index: 1.0,
      vat_percent: 22, // Основная ставка НДС 22% (УСН = 5%, 7% по выбору)

      gbr_enabled: false,
      gbr_object_type: "office",
      gbr_fee_monthly: 7000,
      gbr_months: contractDuration(new Date(defaultStartDate), new Date(defaultEndDate)).billableMonths,
      gbr_armed: false,
    },
  });

  useEffect(() => {
    async function loadData() {
      const [regs, cals] = await Promise.all([getNmckRegions(), getNmckCalendars()]);
      setRegions(regs);
      setCalendars(cals);
      
      if (regs.length > 0) {
        // Auto-select first base region
        const base = regs.find(r => r.name.includes('Базовая')) || regs[0];
        form.setValue("region_id", base.id);
      }
    }
    loadData();
  }, [form]);

  // When region changes, update coefs
  const watchRegion = form.watch("region_id");
  useEffect(() => {
    const reg = regions.find(r => r.id === watchRegion);
    if (reg) {
      form.setValue("regional_coef", reg.regional_coef);
      form.setValue("north_coef", reg.north_coef);
    }
  }, [watchRegion, regions, form]);

  /* <MEMENTO>2026-09-01 (427-ФЗ): antiterror_object — единственный источник правды по слагаемому СТСО.
  Когда флаг выключен, поле «Стоимость СТСО» заблокировано И принудительно обнуляется, поэтому
  values.stso_amount = 0 автоматически во ВСЕХ потребителях (расчёт, share, PDF/HTML/CSV) — отдельные
  проверки флага там не нужны и добавлять их не следует. Основание: на объектах без обязательных
  требований к антитеррористической защищённости монтаж/обслуживание ТСО — отдельный договор
  (п.10 ч.1 ст.6, ч.5 ст.6 427-ФЗ), включать их в НМЦК на охрану нельзя. На объектах С такими
  требованиями ТСО остаются охранной услугой (п.4 ч.2 ст.5) — флаг разблокирует поле.
  Источник: docs/legal-reports/LG-20260831-nmck-ohrana.md. Не заменять «мягким» вариантом (оставить
  ввод, но не считать) — владелец выбрал жёсткую блокировку 01.09.2026.</MEMENTO> */
  const watchAntiterrorObject = form.watch("antiterror_object");
  useEffect(() => {
    if (!watchAntiterrorObject) {
      form.setValue("stso_amount", 0);
    }
  }, [watchAntiterrorObject, form]);

  // Регион для бейджа «типовая НМЦК региона» (ориентир рядом с выбором региона на шаге 1)
  const selectedRegion = regions.find(r => r.id === watchRegion);

  // <MEMENTO>2026-08-22: gbr_months подписан на даты контракта — при изменении start/end срок ГБР
  // пересчитывается через contractDuration().billableMonths (дни пропорционально days/30.5, минимум 1 —
  // решение владельца). Ручная правка срока сбрасывается только при смене дат, что соответствует
  // методике «месяцы по умолчанию = срок контракта». Label для UI считается на лету из дат.</MEMENTO>
  const watchStartDate = form.watch("start_date");
  const watchEndDate = form.watch("end_date");
  useEffect(() => {
    if (watchStartDate && watchEndDate) {
      form.setValue("gbr_months", contractDuration(new Date(watchStartDate), new Date(watchEndDate)).billableMonths);
    }
  }, [watchStartDate, watchEndDate, form]);

  /* <MEMENTO>
  AI_REFLECTION: DEPTH STANDARD
  RATIONALE: applyPreset маппит дизайн-пресеты (ТЦ/Завод/Школа/Склад/Офис) на реальную модель формы. Сбрасывает postConfigs до одного поста с параметрами пресета, ищет регион по подстроке в name, переключает на шаг 2 и скроллит к форме. activePreset подсвечивает активную кнопку.
  CONTEXT_DEPENDENCIES: PRESETS, regions, postConfigs, form
  </MEMENTO> */
  function applyPreset(preset: NmckPreset) {
    setActivePreset(preset.key);
    setPostConfigs([{
      id: typeof crypto !== 'undefined' ? crypto.randomUUID() : '1',
      posts_count: preset.posts_count,
      schedule_hours: preset.schedule_hours,
      u_special_eq: preset.u_special_eq,
      u_armed: preset.u_armed,
      u_mass_events: preset.u_mass_events,
      u_antiterror: preset.u_antiterror,
      u_state_secret: preset.u_state_secret,
    }]);
    const match = regions.find(r =>
      r.name.toLowerCase().includes(preset.regionMatch.toLowerCase())
    );
    if (match) {
      form.setValue("region_id", match.id);
    }
    setActiveTab("step2");
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_preset_' + preset.key);
    }
  }

  /* <MEMENTO>
  AI_REFLECTION: DEPTH STANDARD
  RATIONALE: onSubmit содержит обновлённый пайплайн расчёта НМЦК с индивидуальными U-коэффициентами для каждого поста. Итерация по postConfigs (вместо allGroups + extraGroups), взвешенное среднее для base_hour_rate и adjusted_hour_rate по total_hours. Сохраняет perPostResults для детализации.
  CONTEXT_DEPENDENCIES: calculateContractHours, calculateNmtckAction, postConfigs, formSchema
  </MEMENTO> */
  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setIsPending(true);
      
      const startDate = new Date(values.start_date);
      const endDate = new Date(values.end_date);
      const year = startDate.getFullYear();
      let cal = calendars.find(c => c.year === year);
      
      if (!cal) {
        cal = { year, snr_hours: 164.33, holidays_dates: [] };
      }
      
      const region = regions.find(r => r.id === values.region_id);
      const mrot = values.mrot_override || (region ? region.mrot_value : 27093);
      const snr = values.snr_override || cal.snr_hours;

      /* <MEMENTO>
      AI_REFLECTION: DEPTH STANDARD
      RATIONALE: Итерация по postConfigs с индивидуальными U-коэффициентами. Каждый пост рассчитывается отдельно через RPC calculate_nmtck. Promise.all для параллельного выполнения — N round-trips вместо 1.
      </MEMENTO> */
      const results = await Promise.all(
        postConfigs.map(async (pc) => {
          const hd = calculateContractHours(startDate, endDate, pc.schedule_hours, cal.holidays_dates);
          const p = {
            mrot_value: mrot, regional_coef: values.regional_coef, north_coef: values.north_coef,
            snr_hours: snr,
            total_hours: hd.totalHours, night_hours: hd.nightHours, holiday_hours: hd.holidayHours,
            posts_count: pc.posts_count, schedule_hours: pc.schedule_hours,
            insurance_rate: values.insurance_rate, indirect_rate: values.indirect_rate,
            profit_rate: values.profit_rate,
            stso_amount: 0, insurance_amount: 0,
            u_special_eq: pc.u_special_eq, u_armed: pc.u_armed,
            u_mass_events: pc.u_mass_events, u_antiterror: pc.u_antiterror,
            u_state_secret: pc.u_state_secret,
            inflation_index: 1.0, vat_percent: 0,
            title: values.title
          };
          return calculateNmtckAction(p);
        })
      );

      let aggregated: CalculateNmtckResponse | null = null;
      let totalWeight = 0;
      let weightedBaseRate = 0;
      let weightedAdjustedRate = 0;

      for (const r of results) {
        if (!aggregated) {
          aggregated = { ...r };
          weightedBaseRate = r.base_hour_rate * r.total_hours;
          weightedAdjustedRate = r.adjusted_hour_rate * r.total_hours;
          totalWeight = r.total_hours;
        } else {
          aggregated.total_hours += r.total_hours;
          aggregated.night_addition_total += r.night_addition_total;
          aggregated.holiday_addition_total += r.holiday_addition_total;
          aggregated.vacation_reserve_total += r.vacation_reserve_total;
          aggregated.fot_total += r.fot_total;
          aggregated.sv_total += r.sv_total;
          aggregated.direct_cost_total += r.direct_cost_total;
          aggregated.indirect_cost_total += r.indirect_cost_total;
          aggregated.profit_total += r.profit_total;
          aggregated.posts_count += r.posts_count;
          aggregated.svohr_total += r.svohr_total;
          aggregated.nmtck_total += r.nmtck_total;
          aggregated.vat_amount += r.vat_amount;
          aggregated.total_u_coef = Math.max(aggregated.total_u_coef, r.total_u_coef);
          weightedBaseRate += r.base_hour_rate * r.total_hours;
          weightedAdjustedRate += r.adjusted_hour_rate * r.total_hours;
          totalWeight += r.total_hours;
        }
      }

      if (aggregated) {
        aggregated.base_hour_rate = totalWeight > 0 ? weightedBaseRate / totalWeight : 0;
        aggregated.adjusted_hour_rate = totalWeight > 0 ? weightedAdjustedRate / totalWeight : 0;
        
        const preInflation = aggregated.svohr_total + values.stso_amount + values.insurance_amount;
        const withInflation = preInflation * values.inflation_index;
        const vatAmount = withInflation * (values.vat_percent / 100);
        aggregated.nmtck_total = withInflation + vatAmount;
        aggregated.vat_amount = vatAmount;
      }

      setResult(aggregated);
      setPerPostResults(results);
      setIsPending(false);
      setActiveTab("step3");
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_calculate');
      }
    } catch (error) {
      console.error("NMCC calculation failed:", error);
      setIsPending(false);
      alert("Ошибка расчёта. Попробуйте снова.");
    }
  }

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: CSV-экспорт с BOM (\uFEFF) для корректного отображения кириллицы в Excel. Создание/удаление <a>-элемента через DOM, отзыв ObjectURL через setTimeout. При рефакторинге не менять порядок полей — он соответствует Приказу №45.
  CONTEXT_DEPENDENCIES: result, Yandex.Metrika goal
  </MEMENTO> */
  const handleExportCSV = () => {
    if (!result) return;
    const gbrVals = form.getValues();
    const gbrEnabled = !!gbrVals.gbr_enabled;
    const gbrMonths = Number(gbrVals.gbr_months) || 1;
    const gbrArmed = !!gbrVals.gbr_armed;
    const gbrDur = contractDuration(new Date(gbrVals.start_date), new Date(gbrVals.end_date));
    const gbrLabel = formatGbrDuration(gbrDur.months, gbrDur.days);
    const gbrTotal = gbrEnabled ? calcGbrTotal({
      feeMonthly: Number(gbrVals.gbr_fee_monthly) || 0,
      months: gbrMonths,
      armed: gbrArmed,
    }) : 0;
    const csvContent = 
      "Параметр,Значение,Единица\n" +
      `Количество постов (суммарно),${result.posts_count},шт\n` +
      `Итого часов по контракту,${result.total_hours},ч\n` +
      `Итоговый коэффициент сложности (U),${result.total_u_coef},\n` +
      `Базовая ставка ЗП в час,${(result.base_hour_rate || 0).toFixed(2)},руб\n` +
      `Скорректированная ставка за 1 час,${(result.adjusted_hour_rate || 0).toFixed(2)},руб\n` +
      `Доплата за ночное время (20%),${(result.night_addition_total || 0).toFixed(2)},руб\n` +
      `Доплата за праздничные (100%),${(result.holiday_addition_total || 0).toFixed(2)},руб\n` +
      `Резерв на оплату отпусков,${(result.vacation_reserve_total || 0).toFixed(2)},руб\n` +
      `ФОТ за период,${(result.fot_total || 0).toFixed(2)},руб\n` +
      `Страховые взносы (СВ),${(result.sv_total || 0).toFixed(2)},руб\n` +
      `Прямые затраты (ФОТ + СВ),${(result.direct_cost_total || 0).toFixed(2)},руб\n` +
      `Косвенные расходы (20%),${(result.indirect_cost_total || 0).toFixed(2)},руб\n` +
      `Прибыль (5%),${(result.profit_total || 0).toFixed(2)},руб\n` +
      `СВОХР (Стоимость охраны),${(result.svohr_total || 0).toFixed(2)},руб\n` +
      `НДС / Налог,${(result.vat_amount || 0).toFixed(2)},руб\n` +
      `ИТОГО НМЦК,${(result.nmtck_total || 0).toFixed(2)},руб\n` +
      (gbrEnabled ? `Пультовая охрана с ГБР (${gbrLabel}${gbrArmed ? ', вооруж.' : ''}),${gbrTotal.toFixed(2)},руб\n` : '') +
      (gbrEnabled ? `ИТОГО С УЧЁТОМ ГБР,${((result.nmtck_total || 0) + gbrTotal).toFixed(2)},руб\n` : '');
      
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "nmck_calculation_2026.csv";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_export_csv');
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: Коэффициент вариации (CV) рассчитывается по формуле std/mean*100. Порог однородности 15% зафиксирован п.9 Приказа №45. Выборка < 3 → null. Исправлено: стандартное отклонение по выборке (n-1), а не по генеральной совокупности.
  CONTEXT_DEPENDENCIES: supplierPrices, п.9 Приказа №45
  </MEMENTO> */
  const calculateVariation = (prices: number[]): { cv: number; mean: number; homogeneous: boolean } | null => {
    if (prices.length < 3) return null;
    const n = prices.length;
    const mean = prices.reduce((a, b) => a + b, 0) / n;
    const squaredDiffs = prices.map(p => Math.pow(p - mean, 2));
    const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / (n - 1));
    const cv = (stdDev / mean) * 100;
    return { cv, mean, homogeneous: cv <= 15 };
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: Генерация HTML-отчёта инлайн-строкой без шаблонизатора. Стили встроены в <style> для самодостаточности файла. Формат соответствует требованиям ЕИС к обоснованию НМЦК. При изменении полей синхронизировать с handleExportCSV.
  CONTEXT_DEPENDENCIES: result
  </MEMENTO> */
  const handleExportHTML = () => {
    if (!result) return;

    const gbrVals = form.getValues();
    const gbrEnabled = !!gbrVals.gbr_enabled;
    const gbrMonths = Number(gbrVals.gbr_months) || 1;
    const gbrArmed = !!gbrVals.gbr_armed;
    const gbrDur = contractDuration(new Date(gbrVals.start_date), new Date(gbrVals.end_date));
    const gbrLabel = formatGbrDuration(gbrDur.months, gbrDur.days);
    const gbrTotal = gbrEnabled ? calcGbrTotal({
      feeMonthly: Number(gbrVals.gbr_fee_monthly) || 0,
      months: gbrMonths,
      armed: gbrArmed,
    }) : 0;
    const gbrGrandTotal = (result.nmtck_total || 0) + gbrTotal;
    const gbrHtmlRows = gbrEnabled ? `
                <tr>
                    <td>Пультовая охрана с ГБР (${gbrLabel}${gbrArmed ? ', вооруж.' : ''})</td>
                    <td class="amount">${gbrTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr style="background: #222;">
                    <td><strong>Итого с учётом ГБР</strong></td>
                    <td class="amount"><strong>${gbrGrandTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</strong></td>
                </tr>` : '';

    // Формируем красивый HTML отчет
    const html = `<!DOCTYPE html>
<html lang="ru">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Обоснование НМЦК (Охрана)</title>
    <style>
        body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background: #0a0a0a; color: #f3f4f6; padding: 3rem 1rem; line-height: 1.6; }
        .container { max-width: 800px; margin: 0 auto; background: #111; padding: 2rem; border: 1px solid #333; border-radius: 8px; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
        .header { border-bottom: 1px solid #333; padding-bottom: 1.5rem; margin-bottom: 2rem; text-align: center; }
        .header h1 { font-size: 1.5rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; margin: 0; color: #fff; }
        .header p { color: #888; font-size: 0.9rem; margin-top: 0.5rem; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 2rem; font-family: monospace; font-size: 15px; }
        th, td { padding: 1rem; text-align: left; border-bottom: 1px solid #222; }
        th { color: #888; font-weight: normal; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; width: 60%; }
        td { color: #ddd; }
        td.amount { text-align: right; font-weight: bold; }
        .total-box { background: #1a1a1a; border: 1px solid #444; padding: 2rem; text-align: center; border-radius: 4px; margin-top: 2rem; }
        .total-label { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.2em; color: #888; margin-bottom: 0.5rem; }
        .total-value { font-size: 3rem; font-weight: 300; color: #fff; line-height: 1; }
        .footer { margin-top: 3rem; text-align: center; font-size: 0.8rem; color: #555; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Обоснование НМЦК</h1>
            <p>в соответствии с Приказом Росгвардии от 15.02.2021 № 45 (ред. 2026г)</p>
        </div>
        
        <table>
            <thead>
                <tr>
                    <th>Наименование показателя</th>
                    <th style="text-align: right;">Значение</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Количество круглосуточных постов (эквивалент)</td>
                    <td class="amount">${result.posts_count} шт.</td>
                </tr>
                <tr>
                    <td>Итого часов охраны по контракту (включая ночные и праздничные)</td>
                    <td class="amount">${result.total_hours.toLocaleString('ru-RU')} ч.</td>
                </tr>
                <tr>
                    <td>Итоговый коэффициент сложности (U)</td>
                    <td class="amount">${result.total_u_coef.toFixed(2)}</td>
                </tr>
                <tr>
                    <td>Базовая ставка заработной платы за 1 час рабочего времени</td>
                    <td class="amount">${(result.base_hour_rate || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Скорректированная ставка за 1 час (с учетом районных и северных надбавок)</td>
                    <td class="amount">${(result.adjusted_hour_rate || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Доплата за работу в ночное время (20%) за период контракта</td>
                    <td class="amount">${(result.night_addition_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Доплата за работу в нерабочие праздничные дни (100%) за период контракта</td>
                    <td class="amount">${(result.holiday_addition_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Резерв на оплату отпусков работников</td>
                    <td class="amount">${(result.vacation_reserve_total || 0).toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr style="background: #222;">
                    <td><strong>Фонд оплаты труда (ФОТ) охранников за период</strong></td>
                    <td class="amount"><strong>${result.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</strong></td>
                </tr>
                <tr>
                    <td>Страховые взносы на обязательное социальное страхование (СВ)</td>
                    <td class="amount">${result.sv_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr style="background: #222;">
                    <td><strong>Рп (Прямые затраты) - ФОТ с учетом СВ и коэффициента U</strong></td>
                    <td class="amount"><strong>${result.direct_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</strong></td>
                </tr>
                <tr>
                    <td>Накладные расходы (Косвенные расходы) — 20%</td>
                    <td class="amount">${result.indirect_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Прибыль — 5%</td>
                    <td class="amount">${result.profit_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>СВОХР (Стоимость охраны до налогов)</td>
                    <td class="amount">${result.svohr_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                <tr>
                    <td>Сумма НДС / Налога</td>
                    <td class="amount">${result.vat_amount.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</td>
                </tr>
                ${gbrHtmlRows}
            </tbody>
        </table>

        <div class="total-box">
            <div class="total-label">Итоговая Начальная (максимальная) цена контракта</div>
            <div class="total-value">${result.nmtck_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
        </div>
        
        <div class="footer">
            Документ сформирован автоматически системой управления OXPA.PRO<br>
            Дата формирования: ${new Date().toLocaleDateString('ru-RU')}
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `nmck_report_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    if (typeof window !== 'undefined' && (window as any).ym) {
      (window as any).ym(107253138, 'reachGoal', 'nmck_export_html');
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: PDF-генерация через POST /api/nmck/pdf. Если API упадёт (нет соединения, таймаут), показываем alert. Blob скачивается через динамический <a>. При изменении структуры result синхронизировать API-эндпоинт.
  CONTEXT_DEPENDENCIES: result, /api/nmck/pdf
  </MEMENTO> */
  const handleExportPDF = async () => {
    if (!result) return;
    try {
      const gbrVals = form.getValues();
      const gbrEnabled = !!gbrVals.gbr_enabled;
      const gbrDur = contractDuration(new Date(gbrVals.start_date), new Date(gbrVals.end_date));
      const gbrLabel = formatGbrDuration(gbrDur.months, gbrDur.days);
      const gbrTotal = gbrEnabled ? calcGbrTotal({
        feeMonthly: Number(gbrVals.gbr_fee_monthly) || 0,
        months: Number(gbrVals.gbr_months) || 1,
        armed: !!gbrVals.gbr_armed,
      }) : null;
      const res = await fetch("/api/nmck/pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "Обоснование НМЦК",
          result,
          gbr: gbrEnabled ? {
            enabled: true,
            feeMonthly: Number(gbrVals.gbr_fee_monthly) || 0,
            months: Number(gbrVals.gbr_months) || 1,
            monthsLabel: gbrLabel,
            billedAtMinimum: gbrDur.billedAtMinimum,
            armed: !!gbrVals.gbr_armed,
            total: gbrTotal,
          } : null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "NMTSK_obosnovanie.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_export_pdf');
      }
    } catch (e: any) {
      console.error("PDF export error:", e);
      alert("Ошибка генерации PDF. Попробуйте снова.");
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH STANDARD
  RATIONALE: ensureCalculationSaved — ЕДИНСТВЕННАЯ точка persist расчёта (INSERT nmtck_calculations через
  saveAndGetShareLink). handleShare, кнопка «Сохранить» и кнопка «Запрос КП у ЧОП» переиспользуют её,
  чтобы у флоу запроса КП всегда был настоящий UUID сохранённого расчёта (nmtck_chop_requests.calculation_id
  — FK NOT NULL). Промис кэшируется в ref: параллельные вызовы не дублируют INSERT. savedCalculationId
  сбрасывается при пересчёте (useEffect на result) — старый id относится к другому расчёту.
  CONTEXT_DEPENDENCIES: result, form, regions, calendars, postConfigs, saveAndGetShareLink
  </MEMENTO> */
  const ensureCalculationSaved = useCallback(async (): Promise<string> => {
    if (savedCalculationId) return savedCalculationId;
    if (saveCalculationPromiseRef.current) return saveCalculationPromiseRef.current;
    const promise = (async () => {
      if (!result) throw new Error("Результат расчёта отсутствует");
      const values = form.getValues();
      const region = regions.find(r => r.id === values.region_id);
      const mrot = values.mrot_override || (region ? region.mrot_value : 27093);
      const year = new Date(values.start_date).getFullYear();
      const calendar = calendars.find(c => c.year === year);
      const snr = values.snr_override || calendar?.snr_hours || 164.33;

      const pc = postConfigs[0];
      const hd = pc ? calculateContractHours(
        new Date(values.start_date), new Date(values.end_date),
        pc.schedule_hours, calendar?.holidays_dates || []
      ) : { totalHours: 0, nightHours: 0, holidayHours: 0, daysCount: 0 };

      const id = await saveAndGetShareLink(
        {
          mrot_value: mrot,
          regional_coef: values.regional_coef,
          north_coef: values.north_coef,
          snr_hours: snr,
          total_hours: result.total_hours,
          night_hours: hd.nightHours,
          holiday_hours: hd.holidayHours,
          posts_count: result.posts_count,
          schedule_hours: pc?.schedule_hours || 24,
          insurance_rate: values.insurance_rate,
          indirect_rate: values.indirect_rate,
          profit_rate: values.profit_rate,
          stso_amount: values.stso_amount,
          insurance_amount: values.insurance_amount,
          u_special_eq: pc?.u_special_eq || false,
          u_armed: pc?.u_armed || false,
          u_mass_events: pc?.u_mass_events || false,
          u_antiterror: pc?.u_antiterror || false,
          u_state_secret: pc?.u_state_secret || false,
          inflation_index: values.inflation_index,
          vat_percent: values.vat_percent,
          title: values.title || "Расчет НМЦК",
          // Даты контракта — для точного label срока ГБР на share/PDF (не для расчёта НМЦК)
          start_date: values.start_date,
          end_date: values.end_date,
          // ГБР (рыночная опция) — сохраняем для шаринга /calc/s/[id]
          gbr_enabled: values.gbr_enabled ?? false,
          gbr_object_type: values.gbr_object_type ?? "office",
          gbr_fee_monthly: values.gbr_fee_monthly ?? 7000,
          gbr_months: values.gbr_months ?? 1,
          gbr_armed: values.gbr_armed ?? false,
        },
        result
      );
      setSavedCalculationId(id);
      return id;
    })();
    saveCalculationPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      saveCalculationPromiseRef.current = null;
    }
  }, [savedCalculationId, result, form, regions, calendars, postConfigs]);

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: При пересчёте результат меняется — старый savedCalculationId и уведомление «сохранено»
  относятся к предыдущему расчёту, сбрасываем. Полноценный кабинет заказчика — Фаза 5; сейчас
  «Сохранить» = persist в nmtck_calculations (для залогиненных user_id заполнится в share.ts).
  CONTEXT_DEPENDENCIES: result, savedCalculationId, savedNotice
  </MEMENTO> */
  useEffect(() => {
    setSavedCalculationId(null);
    setSavedNotice(false);
  }, [result]);

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: handleShare переиспользует ensureCalculationSaved (INSERT не дублируется) и дополняет
  флоу копированием ссылки в буфер + Yandex.Metrika goal.
  CONTEXT_DEPENDENCIES: result, ensureCalculationSaved
  </MEMENTO> */
  const handleShare = async () => {
    if (!result) return;
    setShareLoading(true);
    try {
      const id = await ensureCalculationSaved();
      const url = `${window.location.origin}/calc/s/${id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(107253138, 'reachGoal', 'nmck_share');
      }
      alert("Ссылка скопирована! Отправьте её заказчику или коллеге.");
    } catch (e: any) {
      console.error("Share error:", e);
      alert("Не удалось сохранить расчёт. Попробуйте снова.");
    } finally {
      setShareLoading(false);
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: «Запрос КП у ЧОП» сначала сохраняет расчёт (настоящий UUID), затем открывает шаг 4.
  CONTEXT_DEPENDENCIES: result, ensureCalculationSaved
  </MEMENTO> */
  const handleChopRequest = async () => {
    if (!result) return;
    setSavingCalculation(true);
    try {
      await ensureCalculationSaved();
      setActiveTab("step4");
    } catch (e: any) {
      console.error("Chop request error:", e);
      alert("Не удалось сохранить расчёт. Попробуйте снова.");
    } finally {
      setSavingCalculation(false);
    }
  };

  /* <MEMENTO>
  AI_REFLECTION: DEPTH LITE
  RATIONALE: «Сохранить» в шаге 3 — persist расчёта с показом inline-уведомления. Ссылка для доступа
  — через «Поделиться» (копирует в буфер). Полноценный кабинет заказчика — Фаза 5.
  CONTEXT_DEPENDENCIES: result, ensureCalculationSaved
  </MEMENTO> */
  const handleSaveCalculation = async () => {
    if (!result) return;
    setSavingCalculation(true);
    try {
      await ensureCalculationSaved();
      setSavedNotice(true);
    } catch (e: any) {
      console.error("Save error:", e);
      alert("Не удалось сохранить расчёт. Попробуйте снова.");
    } finally {
      setSavingCalculation(false);
    }
  };

  const InfoTooltip = ({ text }: { text: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors cursor-help inline ml-2" />
        </TooltipTrigger>
        <TooltipContent className="bg-[hsl(var(--portal-surface-2))] text-[hsl(var(--portal-text))] border border-[hsl(var(--portal-border))] p-3 max-w-xs text-xs font-mono">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div ref={formRef} className="w-full max-w-5xl mx-auto scroll-mt-20">
      {/* START_PRESETS_UI: Быстрые сценарии расчёта */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--portal-text-muted))] mb-3.5 font-mono">
          Быстрый сценарий — нажмите, чтобы подставить параметры
        </div>
        <div className="flex flex-wrap gap-2.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={
                "flex flex-col items-start gap-1 text-left px-4 py-3 border-2 transition-all font-mono font-semibold text-[13px] uppercase tracking-[0.04em] " +
                (activePreset === p.key
                  ? "border-[hsl(var(--portal-accent))] bg-[hsl(var(--portal-accent-muted))] text-[hsl(var(--portal-accent))]"
                  : "border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text))] hover:border-[hsl(var(--portal-accent))] hover:bg-[hsl(var(--portal-accent-muted))]")
              }
            >
              {p.label}
              <small className="font-sans font-medium text-[11px] normal-case tracking-normal text-[hsl(var(--portal-text-muted))]">
                {p.desc}
              </small>
            </button>
          ))}
        </div>
      </div>
      {/* END_PRESETS_UI */}
      <Form {...form}>
        {/* BUG_FIX_CONTEXT: onError handler обязателен с e.preventDefault() для подавления нативного form submit при ошибках валидации */}
        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(onSubmit as any, (errors) => {
            if (errors.region_id) setActiveTab("step1");
          })(e);
        }} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] rounded-none h-14 p-1">
              <TabsTrigger value="step1" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-[hsl(var(--portal-accent))] data-[state=active]:text-[hsl(var(--portal-accent-on))]">1. Контракт</TabsTrigger>
              <TabsTrigger value="step2" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-[hsl(var(--portal-accent))] data-[state=active]:text-[hsl(var(--portal-accent-on))]">2. Посты</TabsTrigger>
              <TabsTrigger value="step3" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-[hsl(var(--portal-accent))] data-[state=active]:text-[hsl(var(--portal-accent-on))]" disabled={!result}>3. Результат</TabsTrigger>
              <TabsTrigger value="step4" className="rounded-none uppercase tracking-widest text-[10px] data-[state=active]:bg-[hsl(var(--portal-accent))] data-[state=active]:text-[hsl(var(--portal-accent-on))]" disabled={!result}>4. Запрос КП</TabsTrigger>
            </TabsList>
            
            {/* ШАГ 1: Контракт */}
            <TabsContent value="step1" className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] p-6 mt-4 animate-in fade-in-50">
              <div className="space-y-6">
                <div className="flex items-center gap-3 border-b border-[hsl(var(--portal-border))] pb-4">
                  <Calculator className="h-5 w-5 text-[hsl(var(--portal-text))]" />
                  <h3 className="text-sm uppercase tracking-widest text-[hsl(var(--portal-text))] font-bold">Базовые параметры контракта</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField control={form.control as any} name="title" render={({ field }) => (
                    <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Название закупки</FormLabel>
                      <FormControl>
                        <Input className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control as any} name="start_date" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
                        Дата начала
                        <InfoTooltip text="Для точного расчета праздничных и ночных часов, попадающих в этот период." />
                      </FormLabel>
                      <FormControl>
                        <Input type="date" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />
                  
                  <FormField control={form.control as any} name="end_date" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Дата окончания</FormLabel>
                      <FormControl>
                        <Input type="date" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control as any} name="region_id" render={({ field }) => (
                    <FormItem className="col-span-1 md:col-span-2">
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Регион (подстановка МРОТ)</FormLabel>
                      {regions.length > 0 ? (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12">
                              <SelectValue placeholder="Выберите регион" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))]">
                            {regions.map(r => (
                              <SelectItem key={r.id} value={r.id}>{r.name} (МРОТ: {r.mrot_value} ₽)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="h-12 flex items-center bg-[hsl(var(--portal-surface))] text-[hsl(var(--portal-text-muted))] text-xs px-4 animate-pulse">Загрузка регионов...</div>
                      )}
                      {/* <MEMENTO>2026-09-01: бейдж «типовая НМЦК региона» — тот же расчёт, что в сетке городов
                          и на региональных лендингах (lib/typical-nmck.ts, сценарий TYPICAL_NMCK_SCENARIO:
                          1 пост, 24/7, 30 дней, спецсредства Uд1=0.05, НДС 22%). Это ОРИЕНТИР по региону, а не
                          результат текущей формы — при смене параметров формы он НЕ пересчитывается (так и задумано:
                          пользователю нужна опорная точка «сколько обычно стоит пост в этом регионе»).</MEMENTO> */}
                      {selectedRegion && (
                        <div className="flex items-center gap-1 pt-1">
                          <TypicalNmckChip region={selectedRegion} prefix="типовая НМЦК региона" />
                          <InfoTooltip text="Ориентир по региону: 1 пост, круглосуточно, 30-дневный месяц, спецсредства (Uд1=0.05), с НДС 22%. Расчётная величина по Приказу №45, а не рыночная цена — итог вашего расчёта считается по параметрам формы." />
                        </div>
                      )}
                    </FormItem>
                  )} />

                  <div className="col-span-1 md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FormField control={form.control as any} name="regional_coef" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Рай. Коэфф.</FormLabel>
                        <FormControl><Input type="number" step="0.1" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="north_coef" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Северная надб. (%)</FormLabel>
                        <FormControl><Input type="number" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="inflation_index" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Индекс инфляции</FormLabel>
                        <FormControl><Input type="number" step="0.01" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control as any} name="vat_percent" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">НДС (%)</FormLabel>
                        <FormControl><Input type="number" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" {...field} /></FormControl>
                      </FormItem>
                    )} />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="button" onClick={async () => {
                    const valid = await form.trigger(["region_id", "start_date", "end_date"]);
                    if (valid) setActiveTab("step2");
                  }} className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-widest text-[10px] h-12 px-8 font-bold">
                    Далее: Настройка Постов <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ШАГ 2: Посты */}
            <TabsContent value="step2" className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] p-6 mt-4 animate-in fade-in-50">
              <div className="space-y-6">
                {/* Посты с индивидуальными настройками */}
                <div className="flex items-center gap-3 border-b border-[hsl(var(--portal-border))] pb-4">
                  <Settings2 className="h-5 w-5 text-[hsl(var(--portal-text))]" />
                  <h3 className="text-sm uppercase tracking-widest text-[hsl(var(--portal-text))] font-bold">Настройка постов и коэффициентов</h3>
                </div>

                {postConfigs.map((pc, i) => (
                  <div key={pc.id} className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text))] font-bold">
                        Пост {i + 1}{i === 0 ? ' (основной)' : ''}
                      </h4>
                      {i > 0 && (
                        <Button type="button" variant="outline"
                          onClick={() => setPostConfigs(prev => prev.filter((_, j) => j !== i))}
                          className="rounded-none border-[hsl(var(--portal-danger))] text-[hsl(var(--portal-danger))] hover:bg-[hsl(var(--portal-danger))]/10 h-8 px-3 uppercase tracking-widest text-[10px]">
                          Удалить
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] block mb-1">Постов</label>
                        <Input type="number" min={1} max={500} value={pc.posts_count}
                          onChange={e => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, posts_count: parseInt(e.target.value) || 1 } : x))}
                          className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] block mb-1">Часов/сутки</label>
                        <Input type="number" min={1} max={24} value={pc.schedule_hours}
                          onChange={e => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, schedule_hours: parseInt(e.target.value) || 24 } : x))}
                          className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h5 className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-secondary))] flex items-center">
                        <ShieldCheck className="mr-2 h-4 w-4" /> Коэффициенты усложнения (Uд)
                        <InfoTooltip text="Приказ №45, Таблица №2: Uд1=0.05 (спецсредства), Uд2=0.20 (оружие), Uд3=0.30 (массовые мероприятия), Uд4=0.10 (антитеррор), Uд5=0.05 (гостайна)." />
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {([
                          { key: 'u_special_eq' as const, label: 'Спецсредства (+0.05)' },
                          { key: 'u_armed' as const, label: 'Служебное оружие (+0.20)' },
                          { key: 'u_mass_events' as const, label: 'Массовые мероприятия (+0.30)' },
                          { key: 'u_antiterror' as const, label: 'Антитеррор. паспорт (+0.10)' },
                          { key: 'u_state_secret' as const, label: 'Гостайна (+0.05)' },
                        ]).map(({ key, label }) => (
                          <label key={key} className="flex items-center space-x-3 p-2 bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] cursor-pointer">
                            <Checkbox className="border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))]"
                              checked={pc[key]}
                              onCheckedChange={(val) => setPostConfigs(prev => prev.map((x, j) => j === i ? { ...x, [key]: !!val } : x))} />
                            <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-mono">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* BUG_FIX_CONTEXT: e.preventDefault() + stopPropagation обязательны — Button type="button" не всегда блокирует form submit в Next.js/React 18 */}
                <Button type="button" variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPostConfigs(prev => [...prev, {
                      id: typeof crypto !== 'undefined' ? crypto.randomUUID() : String(Date.now()),
                      posts_count: 1, schedule_hours: 24,
                      u_special_eq: false, u_armed: false, u_mass_events: false,
                      u_antiterror: false, u_state_secret: false,
                    }]);
                  }}
                  className="rounded-none border-dashed border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] hover:border-[hsl(var(--portal-border-hover))] h-10 w-full uppercase tracking-widest text-[10px]"
                  aria-label="Добавить пост">
                  + Добавить пост
                </Button>

                {/* Признак объекта по 427-ФЗ — управляет слагаемым СТСО (см. MEMENTO у watchAntiterrorObject) */}
                <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-4 space-y-3">
                  <FormField control={form.control as any} name="antiterror_object" render={({ field }) => (
                    <FormItem className="space-y-0">
                      <label className="flex items-start gap-3 p-2 bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] cursor-pointer">
                        <FormControl>
                          <Checkbox
                            className="mt-0.5 border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))]"
                            checked={!!field.value}
                            onCheckedChange={(val) => field.onChange(!!val)}
                            aria-label="Объект с обязательными требованиями к антитеррористической защищённости"
                          />
                        </FormControl>
                        <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-mono leading-snug">
                          Объект с обязательными требованиями к антитеррористической защищённости
                        </span>
                      </label>
                      <p className="text-[10px] text-[hsl(var(--portal-text-muted))] mt-2 leading-snug">
                        Постановления Правительства РФ о требованиях к антитеррористической защищённости объектов
                        (мест массового пребывания людей, объектов образования, спорта, здравоохранения, ТЭК и др.)
                        + паспорт безопасности объекта. Флаг включает слагаемое СТСО в расчёт НМЦК на охрану.
                      </p>
                      {!field.value && postConfigs.some(pc => pc.u_antiterror) && (
                        <p className="text-[10px] text-[hsl(var(--portal-warn))]/80 mt-2 leading-snug">
                          На посту отмечен коэффициент Uд4 «Антитеррор. паспорт» — если у объекта есть обязательные требования к антитеррористической защищённости, отметьте флаг выше, чтобы учесть ТСО в НМЦК.
                        </p>
                      )}
                    </FormItem>
                  )} />
                </div>

                <div className="grid grid-cols-2 gap-6 pt-2">
                  <FormField control={form.control as any} name="stso_amount" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Стоимость СТСО (руб) <InfoTooltip text="Технические средства охраны (монтаж, обслуживание) за весь период" /></FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          disabled={!watchAntiterrorObject}
                          aria-disabled={!watchAntiterrorObject}
                          className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12 disabled:opacity-40 disabled:cursor-not-allowed"
                          {...field}
                        />
                      </FormControl>
                      {/* <MEMENTO>Формулировка уточнена 31.08.2026 по 427-ФЗ: ТСО остаются охранной услугой
                          на объектах с обязательными требованиями к антитеррористической защищённости (п.4 ч.2 ст.5),
                          на прочих — отдельный договор (п.10 ч.1 ст.6, ч.5 ст.6). Не упрощать до «СТСО закупается отдельно».
                          Источник: docs/legal-reports/LG-20260831-nmck-ohrana.md</MEMENTO> */}
                      {watchAntiterrorObject ? (
                        <p className="text-[10px] text-[hsl(var(--portal-text-muted))] mt-1 leading-snug">
                          Объект отмечен как антитеррористический: проектирование, монтаж и эксплуатационное обслуживание ТСО остаются охранной услугой (п.4 ч.2 ст.5 427-ФЗ) и включаются в НМЦК на охрану.
                        </p>
                      ) : (
                        <p className="text-[10px] text-[hsl(var(--portal-warn))]/80 mt-1 leading-snug">
                          Поле заблокировано: с 01.09.2026 (427-ФЗ) на объектах без обязательных требований к антитеррористической защищённости монтаж и обслуживание ТСО — отдельный договор (п.10 ч.1 ст.6, ч.5 ст.6), в НМЦК на охрану их включать нельзя. Отметьте флаг выше, если объект антитеррористический (п.4 ч.2 ст.5).
                        </p>
                      )}
                    </FormItem>
                  )} />
                  <FormField control={form.control as any} name="insurance_amount" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Страхование жизни (руб) <InfoTooltip text="Дополнительное страхование (если предусмотрено ТЗ)" /></FormLabel>
                      <FormControl><Input type="number" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                {/* Пультовая охрана с ГБР — рыночная опция (вне Приказа №45) */}
                <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-4 space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text))] font-bold flex items-center">
                      <ShieldAlert className="mr-2 h-4 w-4" /> Пультовая охрана с ГБР
                      <InfoTooltip text="Рыночная методика (не входит в Приказ №45): абонплата × срок. Тарифы — oxpa.pro/wiki/gruppa-bystrogo-reagirovaniya" />
                    </h4>
                    <FormField control={form.control as any} name="gbr_enabled" render={({ field }) => (
                      <label className="flex items-center space-x-3 p-2 bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] cursor-pointer">
                        <Checkbox
                          className="border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))]"
                          checked={!!field.value}
                          onCheckedChange={(val) => field.onChange(!!val)}
                        />
                        <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-mono">Включить ГБР</span>
                      </label>
                    )} />
                  </div>

                  {/* <MEMENTO>2026-08-21: при смене типа объекта подставляется дефолтный тариф
                  из GBR_OBJECT_TYPES (вики), пользователь может отредактировать абонплату вручную.
                  gbr_months синхронизируется с датами контракта отдельным useEffect (watch start/end).</MEMENTO> */}
                  {form.watch("gbr_enabled") && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField control={form.control as any} name="gbr_object_type" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Тип объекта</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={(v) => {
                              field.onChange(v);
                              const t = GBR_OBJECT_TYPES.find(t => t.key === v);
                              if (t) form.setValue("gbr_fee_monthly", t.defaultFeeMonthly);
                            }}
                          >
                            <FormControl>
                              <SelectTrigger className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12">
                                <SelectValue placeholder="Выберите тип объекта" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))]">
                              {GBR_OBJECT_TYPES.map(t => (
                                <SelectItem key={t.key} value={t.key}>{t.label} — {t.defaultFeeMonthly.toLocaleString('ru-RU')} ₽/мес</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )} />
                      <FormField control={form.control as any} name="gbr_fee_monthly" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Абонплата (₽/мес)</FormLabel>
                          <FormControl><Input type="number" min={0} className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} /></FormControl>
                          {/* Подсказка нижней границы рынка (решение владельца 2026-08-22:
                              дефолт = нижняя граница вики, пользователь вправе поднять) */}
                          <p className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono">
                            По вики: от {(GBR_OBJECT_TYPES.find(t => t.key === form.watch("gbr_object_type")) || GBR_OBJECT_TYPES[0]).defaultFeeMonthly.toLocaleString('ru-RU')} ₽/мес
                          </p>
                        </FormItem>
                      )} />
                      <FormField control={form.control as any} name="gbr_months" render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Срок (мес)</FormLabel>
                          {/* <MEMENTO>2026-08-22: step="any" обязателен — billableMonths дробный
                              (months + days/30.5), иначе HTML5-валидация (step=1) блокирует submit формы. */}
                          <FormControl><Input type="number" min={1} step="any" className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-12" {...field} /></FormControl>
                          <p className="text-[10px] text-[hsl(var(--portal-text-muted))] font-mono">
                            = {(() => { const d = contractDuration(new Date(watchStartDate), new Date(watchEndDate)); return formatGbrDuration(d.months, d.days); })()} по календарю
                          </p>
                        </FormItem>
                      )} />
                      <FormField control={form.control as any} name="gbr_armed" render={({ field }) => (
                        <FormItem className="flex items-end">
                          <label className="flex items-center space-x-3 p-2 bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] cursor-pointer w-full">
                            <Checkbox
                              className="border-[hsl(var(--portal-border-hover))] data-[state=checked]:bg-[hsl(var(--portal-accent))]"
                              checked={!!field.value}
                              onCheckedChange={(val) => field.onChange(!!val)}
                            />
                            <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-mono">Вооружённая ГБР (+30%)</span>
                          </label>
                        </FormItem>
                      )} />
                    </div>
                  )}
                </div>

                {/* 📐 Предварительный расчёт (сворачиваемый) */}
                {(() => {
                  const pc = postConfigs[0];
                  if (!pc) return null;
                  const startDateVal = form.watch("start_date");
                  const endDateVal = form.watch("end_date");
                  if (!startDateVal || !endDateVal) return null;
                  // BUG_FIX_CONTEXT: Используем реальный календарь для holidayHours (было пустым массивом — январь показывал 0 праздничных)
                  const previewYear = new Date(startDateVal).getFullYear();
                  const previewCal = calendars.find(c => c.year === previewYear);
                  const hd = calculateContractHours(new Date(startDateVal), new Date(endDateVal), pc.schedule_hours, previewCal?.holidays_dates || []);
                  const region = regions.find(r => r.id === form.watch("region_id"));
                  const mrot = form.watch("mrot_override") || (region ? region.mrot_value : 27093);
                  const snrVal = form.watch("snr_override") || previewCal?.snr_hours || 164.33;
                  // BUG_FIX_CONTEXT: client uBase синхронизирован с RPC через calculateUBase (CASE-формула из RPC v4)
                  const uBase = calculateUBase(pc.schedule_hours);
                  const uDopSum = (pc.u_special_eq ? 0.05 : 0) + (pc.u_armed ? 0.20 : 0) + (pc.u_mass_events ? 0.30 : 0) + (pc.u_antiterror ? 0.10 : 0) + (pc.u_state_secret ? 0.05 : 0);
                  const preview = calculateNmtckPreview({
                    mrot, snr: snrVal,
                    regionalCoef: form.watch("regional_coef") || 1,
                    northCoef: form.watch("north_coef") || 0,
                    totalHours: hd.totalHours,
                    nightHours: hd.nightHours,
                    holidayHours: hd.holidayHours,
                    uBase, uDopSum,
                    insuranceRate: form.watch("insurance_rate") || 0.302,
                    indirectRate: form.watch("indirect_rate") || 0.2,
                    profitRate: form.watch("profit_rate") || 0.05,
                    postsCount: pc.posts_count,
                    stsoAmount: form.watch("stso_amount") || 0,
                    insuranceAmount: form.watch("insurance_amount") || 0,
                    inflationIndex: form.watch("inflation_index") || 1,
                    vatPercent: form.watch("vat_percent") || 22,
                  });
                  if (!preview) return null;
                  const lines = [
                    { label: 'БЗП = МРОТ / СНР', formula: `${mrot.toLocaleString('ru-RU')} / ${snrVal}`, value: preview.bzp.toFixed(2) + ' ₽/час' },
                    { label: 'Ставка с учётом РК', formula: `${preview.bzp.toFixed(2)} × ${(form.watch("regional_coef") || 1).toFixed(1)} × (1 + ${(form.watch("north_coef") || 0)}/100)`, value: preview.adjustedHour.toFixed(2) + ' ₽/час' },
                    { label: 'Доплата ночные (20%)', formula: `${preview.adjustedHour.toFixed(2)} × 0.20 × (${hd.nightHours}/${hd.totalHours})`, value: preview.nightAdditionPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Доплата праздничные (100%)', formula: `${preview.adjustedHour.toFixed(2)} × 1.00 × (${hd.holidayHours}/${hd.totalHours})`, value: preview.holidayAdditionPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Резерв отпусков (1/12)', formula: `(${preview.adjustedHour.toFixed(2)} + ${preview.nightAdditionPerHour.toFixed(2)} + ${preview.holidayAdditionPerHour.toFixed(2)}) / 12`, value: preview.vacationReservePerHour.toFixed(2) + ' ₽/час' },
                    { label: 'ФОТ за час', formula: `${preview.adjustedHour.toFixed(2)} + ${preview.nightAdditionPerHour.toFixed(2)} + ${preview.holidayAdditionPerHour.toFixed(2)} + ${preview.vacationReservePerHour.toFixed(2)}`, value: preview.fotPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Страховые взносы (30.2%)', formula: `${preview.fotPerHour.toFixed(2)} × 0.302`, value: preview.svPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Коэффициент U', formula: `${uBase.toFixed(2)} (база) + ${uDopSum.toFixed(2)} (доп)`, value: preview.uTotal.toFixed(2) },
                    { label: 'Прямые затраты за час', formula: `(${preview.fotPerHour.toFixed(2)} + ${preview.svPerHour.toFixed(2)}) × ${preview.uTotal.toFixed(2)}`, value: preview.directPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Накладные (20%)', formula: `${preview.directPerHour.toFixed(2)} × 0.20`, value: preview.indirectPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'Прибыль (5%)', formula: `(${preview.directPerHour.toFixed(2)} + ${preview.indirectPerHour.toFixed(2)}) × 0.05`, value: preview.profitPerHour.toFixed(2) + ' ₽/час' },
                    { label: 'ФОТ за период, все посты', formula: `${preview.fotPerHour.toFixed(2)} × ${hd.totalHours} × ${pc.posts_count}`, value: preview.fotTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2}) + ' ₽' },
                    { label: 'СВОХР за период, все посты', formula: `${preview.svohrPerHour.toFixed(2)} × ${hd.totalHours} × ${pc.posts_count}`, value: preview.svohrTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2}) + ' ₽' },
                  ];
                  return (
                    <details className="group border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))]">
                      <summary className="cursor-pointer list-none p-4 outline-none hover:bg-[hsl(var(--portal-surface-2))] transition-colors flex justify-between items-center text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text))]">
                        <span className="flex items-center">
                          <Sparkles className="mr-2 h-4 w-4" /> Предварительный расчёт
                        </span>
                        <span className="transition-transform duration-300 group-open:rotate-180">
                          <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </summary>
                      <div className="p-4 border-t border-[hsl(var(--portal-border))]">
                        <div className="text-xs font-mono space-y-1">
                          {lines.map((l, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 py-1 border-b border-[hsl(var(--portal-border))] last:border-0">
                              <span className="col-span-4 text-[hsl(var(--portal-text-secondary))] truncate">{l.label}</span>
                              <span className="col-span-5 text-[hsl(var(--portal-text-muted))] truncate text-[10px]">{l.formula}</span>
                              <span className="col-span-3 text-right text-[hsl(var(--portal-text))] font-bold">{l.value}</span>
                            </div>
                          ))}
                          <div className="grid grid-cols-12 gap-2 py-2 mt-2 border-t border-[hsl(var(--portal-border-hover))] font-bold">
                            <span className="col-span-4 text-[hsl(var(--portal-text))]">Предв. НМЦК (без НДС)</span>
                            <span className="col-span-5 text-[hsl(var(--portal-text-muted))] text-[10px]">{preview.svohrTotal.toLocaleString('ru-RU')} × {form.watch("inflation_index") || 1}</span>
                            <span className="col-span-3 text-right text-[hsl(var(--portal-text))]">{preview.nmtckPreVat.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                          <div className="grid grid-cols-12 gap-2 py-1">
                            <span className="col-span-4 text-[hsl(var(--portal-text))]">НДС ({(form.watch("vat_percent") || 22)}%)</span>
                            <span className="col-span-5 text-[hsl(var(--portal-text-muted))] text-[10px]">{preview.nmtckPreVat.toLocaleString('ru-RU')} × {(form.watch("vat_percent") || 22) / 100}</span>
                            <span className="col-span-3 text-right text-[hsl(var(--portal-text))]">{preview.vatAmount.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                          <div className="grid grid-cols-12 gap-2 py-1 font-bold text-sm">
                            <span className="col-span-7 text-[hsl(var(--portal-text))]">Предв. НМЦК итого</span>
                            <span className="col-span-5 text-right text-[hsl(var(--portal-accent))]">{preview.nmtckTotal.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span>
                          </div>
                        </div>
                      </div>
                    </details>
                  );
                })()}

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={() => setActiveTab("step1")} className="rounded-none border-[hsl(var(--portal-border-hover))] bg-transparent text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] h-10 w-10 p-0">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button type="submit" className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-widest text-[10px] h-12 px-8 font-bold ml-auto" disabled={isPending}>
                    {isPending ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : <Sparkles className="mr-3 h-4 w-4" />}
                    {isPending ? "ВЫЧИСЛЕНИЕ..." : "РАССЧИТАТЬ НМЦК"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ШАГ 3: Результат */}
            <TabsContent value="step3" className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] p-6 mt-4 animate-in fade-in-50">
              {result ? (
                <div className="space-y-6">
                  <div className="p-6 bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] group">
                    <div className="text-[10px] font-bold text-[hsl(var(--portal-text))] border border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-bg))] uppercase tracking-[0.2em] mb-4 inline-block px-3 py-1">
                      ОБОСНОВАНИЕ СФОРМИРОВАНО
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-1">
                      Итоговая Начальная (максимальная) цена контракта (НМЦК)
                    </div>
                    <div className="text-4xl md:text-5xl font-light text-[hsl(var(--portal-text))] tracking-tight font-mono break-all py-2">
                      {result.nmtck_total.toLocaleString('ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₽
                    </div>
                  </div>

                  {/* Пультовая охрана с ГБР — вторичный блок (НМЦК по Приказу 45 не подменяет) */}
                  {form.watch("gbr_enabled") && (() => {
                    const gbrFee = Number(form.watch("gbr_fee_monthly")) || 0;
                    const gbrMonths = Number(form.watch("gbr_months")) || 1;
                    const gbrDur = contractDuration(new Date(form.watch("start_date")), new Date(form.watch("end_date")));
                    const gbrLabel = formatGbrDuration(gbrDur.months, gbrDur.days);
                    const gbrArmed = !!form.watch("gbr_armed");
                    const gbrTotal = calcGbrTotal({ feeMonthly: gbrFee, months: gbrMonths, armed: gbrArmed });
                    const grandTotal = result.nmtck_total + gbrTotal;
                    const fmtGbr = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return (
                      <div className="p-6 bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))]">
                        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-2">
                          Пультовая охрана с ГБР (рыночная опция, вне Приказа №45)
                        </div>
                        <div className="text-lg md:text-xl font-mono text-[hsl(var(--portal-text))] break-all">
                          {fmtGbr(gbrFee)} ₽/мес × {gbrLabel}{gbrDur.billedAtMinimum ? ' (минимум — 1 мес)' : ''}{gbrArmed ? ' × 1.3 (вооруж.)' : ''} = <span className="font-bold text-[hsl(var(--portal-accent))]">{fmtGbr(gbrTotal)} ₽</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-[hsl(var(--portal-border))] text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-secondary))]">
                          ИТОГО С УЧЁТОМ ГБР: <span className="text-[hsl(var(--portal-text))] font-bold text-sm">{fmtGbr(grandTotal)} ₽</span>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))]">
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-1">Часов всего</div>
                      <div className="text-xl font-mono text-[hsl(var(--portal-text))]">{result.total_hours.toLocaleString('ru-RU')} ч.</div>
                    </div>
                    <div className="p-4 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))]">
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-1">Коэфф U.</div>
                      <div className="text-xl font-mono text-[hsl(var(--portal-text))]">{result.total_u_coef.toFixed(2)}</div>
                    </div>
                    <div className="p-4 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))]">
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-1">Прибыль (5%)</div>
                      <div className="text-xl font-mono text-[hsl(var(--portal-text))]">{result.profit_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
                    </div>
                    <div className="p-4 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))]">
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-1">ФОТ (Зарплата)</div>
                      <div className="text-xl font-mono text-[hsl(var(--portal-text))]">{result.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</div>
                    </div>
                  </div>

                  {/* Структура цены — stacked bar + легенда + мета + детальная разбивка */}
                  <CostBreakdownBar result={result} />

                  {perPostResults.length > 1 && (
                    <Accordion type="single" collapsible className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))]">
                      <AccordionItem value="breakdown">
                        <AccordionTrigger className="px-4 py-3 text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text))] hover:no-underline">
                          Детализация по постам ({perPostResults.length} постов)
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-4">
                            {perPostResults.map((r, i) => (
                              <div key={i} className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg))] p-3">
                                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-2">Пост {i + 1}</div>
                                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">Часов: </span><span className="text-[hsl(var(--portal-text))]">{r.total_hours.toLocaleString('ru-RU')}</span></div>
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">Коэфф U: </span><span className="text-[hsl(var(--portal-text))]">{r.total_u_coef.toFixed(2)}</span></div>
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">ФОТ: </span><span className="text-[hsl(var(--portal-text))]">{r.fot_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">СВ: </span><span className="text-[hsl(var(--portal-text))]">{r.sv_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">Прямые затраты: </span><span className="text-[hsl(var(--portal-text))]">{r.direct_cost_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                  <div><span className="text-[hsl(var(--portal-text-muted))]">СВОХР: </span><span className="text-[hsl(var(--portal-text))]">{r.svohr_total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} ₽</span></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                  
                  {/* Коэффициент вариации (п.9 Приказа №45) */}
                  <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] p-4 space-y-3">
                    <h4 className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text))] flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2" /> Коэффициент вариации цен (п.9 Приказа №45)
                      <InfoTooltip text="Согласно п.9 Порядка: при сборе цен от ≥3 поставщиков рассчитывается коэффициент вариации. Если > 15% — цены неоднородны, нужны дополнительные запросы." />
                    </h4>
                    <p className="text-xs text-[hsl(var(--portal-text-muted))] font-mono">
                      Введите коммерческие предложения от поставщиков (минимум 3) для проверки однородности цен.
                    </p>
                    
                    <div className="flex gap-2">
                      <Input 
                        type="number" 
                        placeholder="Цена поставщика (₽)" 
                        value={newSupplierPrice}
                        onChange={(e) => setNewSupplierPrice(e.target.value)}
                        className="rounded-none bg-[hsl(var(--portal-bg))] border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] font-mono h-10 flex-1"
                      />
                      <Button 
                        type="button"
                        onClick={() => {
                          const price = parseFloat(newSupplierPrice);
                          if (!isNaN(price) && price > 0) {
                            setSupplierPrices(prev => [...prev, price]);
                            setNewSupplierPrice('');
                          }
                        }}
                        className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] h-10 px-4 uppercase tracking-widest text-[10px] font-bold"
                      >
                        + Добавить
                      </Button>
                    </div>
                    
                    {supplierPrices.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {supplierPrices.map((p, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border-hover))] text-xs font-mono text-[hsl(var(--portal-text))]">
                              #{i + 1}: {p.toLocaleString('ru-RU')} ₽
                              <button 
                                type="button"
                                onClick={() => setSupplierPrices(prev => prev.filter((_, j) => j !== i))}
                                className="text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] ml-1"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        
                        {supplierPrices.length >= 3 && (() => {
                          const v = calculateVariation(supplierPrices)!;
                          return (
                            <div className={`p-3 border ${v.homogeneous ? 'border-[hsl(var(--portal-accent))] bg-[hsl(var(--portal-accent-muted))]' : 'border-[hsl(var(--portal-danger))] bg-[hsl(var(--portal-danger))]/10'}`}>
                              <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Средняя цена</div>
                                  <div className="text-lg font-mono text-[hsl(var(--portal-text))]">{v.mean.toLocaleString('ru-RU', {maximumFractionDigits: 0})} ₽</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Коэфф. вариации</div>
                                  <div className={`text-lg font-mono ${v.homogeneous ? 'text-[hsl(var(--portal-accent))]' : 'text-[hsl(var(--portal-danger))]'}`}>{v.cv.toFixed(1)}%</div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">Статус</div>
                                  <div className={`text-sm font-mono font-bold uppercase ${v.homogeneous ? 'text-[hsl(var(--portal-accent))]' : 'text-[hsl(var(--portal-danger))]'}`}>
                                    {v.homogeneous ? 'Однородны ✓' : 'Неоднородны ✗'}
                                  </div>
                                </div>
                              </div>
                              {!v.homogeneous && (
                                <p className="text-xs text-[hsl(var(--portal-danger))] mt-2 font-mono">
                                  Коэффициент вариации превышает 15%. Согласно п.9 Приказа №45, рекомендуется направить дополнительные запросы ценовой информации.
                                </p>
                              )}
                            </div>
                          );
                        })()}
                        
                        {supplierPrices.length > 0 && supplierPrices.length < 3 && (
                          <p className="text-xs text-[hsl(var(--portal-text-muted))] font-mono">
                            Добавьте минимум 3 цены для расчёта коэффициента вариации (добавлено: {supplierPrices.length}).
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col md:flex-row gap-4 pt-4">
                    <Button type="button" variant="outline" onClick={() => setActiveTab("step2")} className="rounded-none border-[hsl(var(--portal-border-hover))] bg-transparent text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] h-12 md:h-12 md:w-12 p-0 shrink-0 flex items-center justify-center">
                      <ArrowLeft className="h-4 w-4" />
                      <span className="md:hidden ml-2 uppercase tracking-widest text-[10px] font-bold">Назад</span>
                    </Button>
                    <div className="flex flex-col sm:flex-row gap-2 flex-1">
                      <Button
                        type="button"
                        onClick={handleChopRequest}
                        disabled={!result || savingCalculation}
                        className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] disabled:bg-[hsl(var(--portal-surface-2))] disabled:text-[hsl(var(--portal-text-dim))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors"
                      >
                        {savingCalculation ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />} Запрос КП у ЧОП
                      </Button>
                      <Button type="button" onClick={handleShare} disabled={shareLoading}
                        className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        {shareLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
                        Поделиться
                      </Button>
                      <Button type="button" onClick={handleSaveCalculation} disabled={savingCalculation}
                        className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        {savingCalculation ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Сохранить
                      </Button>
                      <Button type="button" onClick={handleExportPDF} className="rounded-none bg-transparent border border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] hover:border-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        <Download className="mr-2 h-4 w-4" /> PDF (ЕИС)
                      </Button>
                      <Button type="button" onClick={handleExportHTML} className="rounded-none bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent))] hover:bg-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent-on))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold transition-colors">
                        <Download className="mr-2 h-4 w-4" /> HTML Отчет
                      </Button>
                      <Button type="button" onClick={handleExportCSV} className="rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-widest text-[10px] h-12 flex-1 font-bold">
                        <Download className="mr-2 h-4 w-4" /> CSV Таблица
                      </Button>
                    </div>
                  </div>
                  {savedNotice && (
                    <p className="text-xs text-[hsl(var(--portal-accent))] font-mono mt-2 p-2 bg-[hsl(var(--portal-accent-muted))] border border-[hsl(var(--portal-accent-muted))]" data-testid="saved-notice">
                      Расчёт сохранён. Ссылка для доступа — кнопка «Поделиться».
                    </p>
                  )}
                  {shareUrl && (
                    <p className="text-xs text-[hsl(var(--portal-accent))] font-mono mt-2 p-2 bg-[hsl(var(--portal-accent-muted))] border border-[hsl(var(--portal-accent-muted))]">
                      Ссылка скопирована: <span className="text-[hsl(var(--portal-text))]">{shareUrl}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 text-[hsl(var(--portal-text-dim))] flex flex-col items-center">
                  <Calculator className="h-12 w-12 mb-4 opacity-50" />
                  <p className="max-w-[250px] text-xs font-mono uppercase tracking-widest">ДЛЯ ПРОСМОТРА РЕЗУЛЬТАТА ВЫПОЛНИТЕ ШАГ 2</p>
                </div>
              )}
            </TabsContent>

            {/* ШАГ 4: Запрос КП у ЧОП */}
            <TabsContent value="step4" className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border))] p-6 mt-4 animate-in fade-in-50">
              {result ? (
                <ChopRequestStep
                  calculationId={savedCalculationId ?? undefined}
                  nmckTotal={result.nmtck_total}
                  posts={`${result.posts_count} постов, ${result.total_hours} часов, U=${result.total_u_coef.toFixed(2)}`}
                  period={`${form.watch("start_date")} — ${form.watch("end_date")}`}
                  region={regions.find(r => r.id === form.watch("region_id"))?.name}
                  onBack={() => setActiveTab("step3")}
                />
              ) : (
                <div className="text-center py-16 text-[hsl(var(--portal-text-dim))] flex flex-col items-center">
                  <Calculator className="h-12 w-12 mb-4 opacity-50" />
                  <p className="max-w-[250px] text-xs font-mono uppercase tracking-widest">СНАЧАЛА ВЫПОЛНИТЕ РАСЧЁТ НМЦК</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </form>
      </Form>
    </div>
  );
};
